import { Link } from "react-router-dom";
import { Seo, Eyebrow, ArrowLink } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { POSTS } from "@/data/blog";

export default function Publications() {
  return (
    <div data-testid="publications-page">
      <Seo title="Publications" description="Papers, lab notes and reproducible benchmarks from DataFrontier Research." />
      <PageHero
        eyebrow="Research · Publications"
        title="We publish the harness, not just the headline."
        lead="Lab notes, benchmarks and methodology. Every metric we cite is reproducible — the evaluation harness ships with the open-source core."
      />

      <Section>
        <Reveal>
          <Eyebrow>Benchmarks & reproducibility</Eyebrow>
        </Reveal>
        <Stagger className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
          {[
            { v: "0.958", l: "Recall@10 on a committed labelled set (semantic + temporal)" },
            { v: "100%", l: "chunk-hash reproducibility on re-ingest" },
            { v: "0", l: "required cloud calls to produce an answer" },
          ].map((s) => (
            <Item key={s.l} className="bg-ink p-8">
              <div className="font-serif text-4xl text-bone">{s.v}</div>
              <p className="mt-3 text-sm leading-relaxed text-mist">{s.l}</p>
            </Item>
          ))}
        </Stagger>
        <Reveal delay={0.1} className="mt-8">
          <ArrowLink href="https://pypi.org/project/upii/" external>
            Reproduce the evaluation on PyPI
          </ArrowLink>
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Lab notes</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">The DF series</h2>
        </Reveal>
        <Stagger className="mt-12 divide-y divide-white/10 border-t border-white/10">
          {POSTS.map((p) => (
            <Item key={p.id}>
              <Link to={`/blog/${p.slug}`} className="group grid gap-3 py-7 md:grid-cols-12 md:gap-6">
                <div className="font-mono text-xs text-saffron md:col-span-2">{p.id}</div>
                <h3 className="font-serif text-lg leading-snug text-bone transition-colors duration-300 group-hover:text-saffron md:col-span-7 md:text-xl">
                  {p.title}
                </h3>
                <div className="font-mono text-xs text-mist md:col-span-3 md:text-right">
                  {new Date(p.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              </Link>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="max-w-2xl">
          <Eyebrow>Intellectual property</Eyebrow>
          <p className="mt-6 text-[17px] leading-relaxed text-mist">
            DataFrontier has filed a patent application covering core methods in its unified personal and enterprise
            intelligence infrastructure. The application is pending; to protect the integrity of the process, we do not
            discuss claims or methods beyond what is published in our open-source releases.
          </p>
          <div className="mt-8">
            <ArrowLink to="/company/patents">On our patent</ArrowLink>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
